import { isProd } from "../../utils/env";
import API_ROUTES from "../../services/ajax/routes";
import type { MapViewExperienceResponse, RequestedListing } from "../../@types/mapviewxs";
import updateMapSearchParams from "../../utils/search-params-update";
import { validatedZipcode } from "../../utils/validation";
import { calculateRadiusForVisibleRegion } from "./helpers";
import { getZipCodeFromLatLng } from "./geocoding";
import type { LoadLocationDataParams, MapViewModules } from "./types";

type LoadMoreOptions = {
  listings?: RequestedListing[];
  csrfToken?: string;
};

export const loadDataForLocation = async ({
  zipcode,
  distance = getDistance(),
  sources = getSources(),
}: LoadLocationDataParams): Promise<MapViewModules | undefined> => {
  const isMock = !isProd() && window.location.search.includes("mock");
  const searchParams = new URLSearchParams(isMock ? { mock: "default" } : { zipcode, distance, sources });
  const endpoint = `${API_ROUTES.LOAD_MORE}?${searchParams.toString()}`;
  const fetchResult = await fetch(endpoint);

  if (fetchResult.status !== 200) throw new Error("Failed to load more markers");

  const mapViewData: MapViewExperienceResponse = await fetchResult.json();
  updateMapSearchParams({ zipcode, distance, sources });

  return mapViewData.modules;
};

export const loadMore = async (map: google.maps.Map, apiKey: string): Promise<MapViewModules | undefined> => {
  const region = map.getBounds()!;
  const center = region.getCenter();
  const distance = calculateRadiusForVisibleRegion(region);
  const zipcode = await getZipCodeFromLatLng(apiKey, center, region);
  const mapViewData = await loadDataForLocation({ zipcode, distance });
  return mapViewData;
};

export const loadListings = async (options: LoadMoreOptions): Promise<MapViewModules | undefined> => {
  const zipcode = getZipCode();
  const distance = getDistance();
  const entryPageId = getEntryPageId();
  const isMock = !isProd() && window.location.search.includes("mock");
  const searchParams = new URLSearchParams(isMock ? { mock: "default" } : { zipcode, distance, entryPageId });
  const endpoint = `${API_ROUTES.LOAD_MORE}?${searchParams.toString()}`;
  const authHeaders: Record<string, string> = options?.csrfToken ? { srt: options.csrfToken } : {};
  const body = JSON.stringify({ listings: options?.listings });
  const fetchResult = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body,
  });

  if (fetchResult.status !== 200) throw new Error("Failed to load listings");

  const mapViewData: MapViewExperienceResponse = await fetchResult.json();

  return mapViewData.modules;
};

export const getSources = (): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get("sources") || "";
};

export const getDistance = (): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get("distance") || "";
};

export const getZipCode = (): string => {
  const params = new URLSearchParams(window.location.search);
  return validatedZipcode(params.get("zipcode")) || "";
};

const getEntryPageId = (): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get("entryPageId") || "";
};
